/**
 * SubscriptionContext - Centralny context do zarządzania subskrypcjami
 *
 * Pobiera status subskrypcji firmy raz i udostępnia go wszystkim komponentom.
 * Eliminuje potrzebę wielokrotnego pobierania danych z API.
 */

import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import api from '../services/apiService';
import { UserContext } from './UserContext';
import { SUBSCRIPTION_KEYS, hasAccessToMenuItem, hasAccessToRoute, filterMenuBySubscriptions } from '../config/subscriptionConfig';

export const SubscriptionContext = createContext();

export const SubscriptionProvider = ({ children }) => {
  const { user, loading: userLoading } = useContext(UserContext);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pobierz status subskrypcji
  const fetchSubscriptionStatus = useCallback(async () => {
    if (!user?.id || !user?.accessToken) {
      // If UserContext finished loading and there's no user, stop loading
      // This allows RouteGuard/PrivateRoute to redirect to login
      if (!userLoading) {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/company/subscription-status/');
      setSubscriptionStatus(response.data);
    } catch (err) {
      console.error('Error fetching subscription status:', err);
      setError(err.message || 'Failed to load subscription status');
      // Ustaw domyślne wartości w przypadku błędu
      setSubscriptionStatus({
        has_any_subscription: false,
        subscription_checks: {},
        active_subscriptions: [],
        subscription_types: [],
      });
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.accessToken, userLoading]);

  // Pobierz status przy montowaniu i gdy zmieni się użytkownik
  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  // Refresh subscription status (można wywołać po zmianie subskrypcji)
  const refreshSubscriptionStatus = useCallback(() => {
    return fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  /**
   * Sprawdza czy firma ma konkretną subskrypcję
   * @param {number} typeId - ID typu subskrypcji (z SUBSCRIPTION_TYPES)
   * @returns {boolean}
   */
  const hasSubscription = useCallback((typeId) => {
    if (loading || !subscriptionStatus?.subscription_checks) {
      return false;
    }
    const key = SUBSCRIPTION_KEYS[typeId];
    return subscriptionStatus.subscription_checks[key] === true;
  }, [loading, subscriptionStatus]);

  /**
   * Sprawdza czy firma ma KTÓRĄKOLWIEK z podanych subskrypcji
   * @param {number[]} typeIds - Tablica ID typów subskrypcji
   * @returns {boolean}
   */
  const hasAnySubscription = useCallback((typeIds = []) => {
    if (typeIds.length === 0) {
      return subscriptionStatus?.has_any_subscription || false;
    }
    return typeIds.some(typeId => hasSubscription(typeId));
  }, [hasSubscription, subscriptionStatus]);

  /**
   * Sprawdza czy firma ma WSZYSTKIE podane subskrypcje
   * @param {number[]} typeIds - Tablica ID typów subskrypcji
   * @returns {boolean}
   */
  const hasAllSubscriptions = useCallback((typeIds) => {
    return typeIds.every(typeId => hasSubscription(typeId));
  }, [hasSubscription]);

  /**
   * Sprawdza czy użytkownik ma dostęp do elementu menu
   * @param {Object} menuItem - Element z ADMIN_MENU lub USER_MENU
   * @returns {boolean}
   */
  const canAccessMenuItem = useCallback((menuItem) => {
    return hasAccessToMenuItem(menuItem, subscriptionStatus?.subscription_checks, user);
  }, [subscriptionStatus, user]);

  /**
   * Sprawdza czy użytkownik ma dostęp do trasy
   * @param {string} pathname - Ścieżka do sprawdzenia
   * @returns {boolean}
   */
  const canAccessRoute = useCallback((pathname) => {
    return hasAccessToRoute(pathname, subscriptionStatus?.subscription_checks);
  }, [subscriptionStatus]);

  /**
   * Filtruje menu na podstawie subskrypcji
   * @param {Array} menu - Tablica elementów menu
   * @returns {Array} - Przefiltrowane menu
   */
  const filterMenu = useCallback((menu) => {
    return filterMenuBySubscriptions(menu, subscriptionStatus?.subscription_checks, user);
  }, [subscriptionStatus, user]);

  /**
   * Pobiera listę aktywnych typów subskrypcji
   * @returns {Object[]}
   */
  const getActiveSubscriptionTypes = useCallback(() => {
    return subscriptionStatus?.subscription_types || [];
  }, [subscriptionStatus]);

  /**
   * Pobiera szczegóły subskrypcji (daty wygaśnięcia itp.)
   * @returns {Object[]}
   */
  const getActiveSubscriptions = useCallback(() => {
    return subscriptionStatus?.active_subscriptions || [];
  }, [subscriptionStatus]);

  const value = {
    // Stan
    subscriptionStatus,
    loading,
    error,

    // Podstawowe sprawdzenia
    hasSubscription,
    hasAnySubscription,
    hasAllSubscriptions,

    // Sprawdzenia dla menu i tras
    canAccessMenuItem,
    canAccessRoute,
    filterMenu,

    // Gettery
    getActiveSubscriptionTypes,
    getActiveSubscriptions,

    // Akcje
    refreshSubscriptionStatus,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
